import { FieldContinuationDataForResolver, FieldTemplateForResolver, FormTemplate, FormTemplateForResolver, Option } from "db/definitions";
import { getTemplateData } from "db/merge";
import {
	bulkSaveFieldContinuationDatas,
	deleteFieldContinuationDatasByFormTemplate,
	getFieldContinuationDatasByFormTemplate,
} from "db/queries/fieldContinuationDataQueries";
import {
	bulkRetrieveFieldTemplatesByFormTemplate,
	bulkSaveFieldTemplates,
	createFieldTemplateForBatchResolver,
	deleteFieldTemplatesByFormTemplate,
} from "db/queries/fieldTemplateQueries";
import { bulkSaveFormTemplates, createFormTemplateForBatchResolver, deleteFormTemplateById, getFormTemplateById } from "db/queries/formTemplateQueries";
import { bulkRetrieveOptionsByFormTemplateId, bulkSaveOptions, deleteOptionsByFormTemplate } from "db/queries/optionQueries";
import batchCreateFieldContinuationData from "./batchCreateFieldContinuationData";
import batchCreateFieldTemplates from "./batchCreateFieldTemplates";
import batchCreateFormTemplates from "./batchCreateFormTemplates";
import batchCreateOptions from "./batchCreateOptions";
import { handleBatchCrud } from "./handleBatchCrud";
import handleBatchGetFormTemplates from "./handleBatchGetFormTemplates";

function deDupById<T extends { id: string }>(documents: T[]) {
	const unique = new Set();
	return documents.filter((doc) => {
		if (!doc || unique.has(doc.id)) return false;
		unique.add(doc.id);
		return true;
	});
}

/**
 * This actually handles sving the form template and its children offline.
 * @param selectedTemplates that need to be saved offline.
 */
export async function syncSelectedTemplatesOffline(selectedTemplates: FormTemplate[]) {
	const selected = selectedTemplates.map((template) => template.id);

	const retrievedFormTemplates = await handleBatchGetFormTemplates(selected);
	// these arrays need to be promises.
	const finalFormTemplates: FormTemplateForResolver[] = [];
	const finalFieldTemplates: FieldTemplateForResolver[] = [];
	const finalOptions: Option[] = [];
	const finalContinuationDatas: FieldContinuationDataForResolver[] = [];
	const templatePromises = [];
	// now gotta loop through each form template
	for (const formTemplate of retrievedFormTemplates) {
		finalFormTemplates.push(createFormTemplateForBatchResolver(formTemplate));
		templatePromises.push(getTemplateData(formTemplate.id, true, finalFieldTemplates, finalContinuationDatas, finalOptions));
	}

	await Promise.all(templatePromises);
	// add the paginated options intot he main list of options

	await Promise.all([
		bulkSaveFormTemplates(finalFormTemplates),
		bulkSaveFieldTemplates(finalFieldTemplates),
		bulkSaveOptions(finalOptions),
		bulkSaveFieldContinuationDatas(finalContinuationDatas),
	]);
}

/**
 * Pulls the selected templates from offline db and put them online.
 */
export async function syncSelectedTemplatesOnline(selectedTemplates: FormTemplate[]) {
	const selected = selectedTemplates.map((template) => template.id);
	//make mini method to convert from array of tempaltes to array of ids -> use for both larger methods

	const formTemplatePromises = [];
	const fieldTemplatePromises = [];
	const optionPromises = [];
	const fieldContinuationDataPromises = [];

	for (let i = 0; i < selected.length; i++) {
		const formTemplateId = selected[i];
		fieldTemplatePromises.push(bulkRetrieveFieldTemplatesByFormTemplate(formTemplateId));
		optionPromises.push(bulkRetrieveOptionsByFormTemplateId(formTemplateId));
		formTemplatePromises.push(getFormTemplateById(formTemplateId));
		fieldContinuationDataPromises.push(getFieldContinuationDatasByFormTemplate(formTemplateId));
	}
	const [resolvedFieldTemplates, resolvedFormTemplates, resolvedOptions, resolvedFieldContinuations] = await Promise.all([
		await Promise.all(fieldTemplatePromises),
		await Promise.all(formTemplatePromises),
		await Promise.all(optionPromises),
		await Promise.all(fieldContinuationDataPromises),
	]);
	const filteredFormTemplates = resolvedFormTemplates.filter((item): item is FormTemplateForResolver => item !== undefined);
	const dedupedFormTemplates = deDupById(filteredFormTemplates);
	const fieldTemplatesForResolver = [];
	const uniqueFields = new Set();
	for (const fieldTemplateArray of resolvedFieldTemplates) {
		for (const field of fieldTemplateArray) {
			if (!uniqueFields.has(field.id)) {
				uniqueFields.add(field.id);
				fieldTemplatesForResolver.push(createFieldTemplateForBatchResolver(field));
			}
		}
	}

	const finalOptions = resolvedOptions.flat();
	const finalFieldContinuations = resolvedFieldContinuations.flat();
	handleBatchCrud(dedupedFormTemplates, batchCreateFormTemplates, 25);
	handleBatchCrud(fieldTemplatesForResolver, batchCreateFieldTemplates, 25);
	handleBatchCrud(finalOptions, batchCreateOptions, 25);
	if (resolvedFieldContinuations.length) handleBatchCrud(finalFieldContinuations, batchCreateFieldContinuationData, 25);
}

/**
 * Given the templates we want to uninstall, uninstalls them and their children
 * @author Eric Webb <ewebb@factorearth.com>
 * @param selectedTemplates The templates we want to uninstall
 */
export async function uninstallSelectedTemplates(selectedTemplates: FormTemplate[]) {
	const promises = [];
	for (const template of selectedTemplates) {
		promises.push(deleteFormTemplateById(template.id));
		promises.push(deleteFieldTemplatesByFormTemplate(template.id));
		promises.push(deleteOptionsByFormTemplate(template.id));
		promises.push(deleteFieldContinuationDatasByFormTemplate(template.id));
	}
	await Promise.all(promises);
}
